import {SignalRMethod} from "@api/repositories/reatime-repository/types";
import {ObserverBase} from "@share/web-conferences/signal-r/observers/ObserverBase";
import {ObserverData} from "@share/web-conferences/interfaces/signalr/ObserverData";
import {JoinHandlerInterface} from "@share/web-conferences/interfaces/JoinHandlerInterface";
import {RealtimeUpdate} from "@share/web-conferences/types/RealtimeUpdate";
import {
    addPresenterAction,
    addRemovedSpeakers,
    removePresenterAction,
    setPresentersAction
} from "@share/web-conferences/store/actions";
import {UserProfile} from "@api/models/UserProfile";
import {SetPresentersParam} from "@share/web-conferences/types/SetPresentersParam";
import {UserRoleType} from "@share/web-conferences/enums/UserRoleType";
import {AddPresentersParam} from "@share/web-conferences/types/AddPresentersParam";
import {WebConferenceStateHelper} from "@share/web-conferences/helpers/WebConferenceStateHelper";
import {StoreParameters} from "@share/web-conferences/types/StoreParameters";
import { LiveKitClientInterface } from "@share/live-kit/interfaces/LiveKitClientInterface";
import { WebConferenceService } from "@share/web-conferences/data/WebConferenceService";
import { StoreHelper } from "@share/web-conferences/helpers/StoreHelper";
import { LiveKitParticipantsEventsHandler } from "@share/live-kit/live-kit-classes/LiveKitParticipantsEventsHandler";

export class PresenterObserver extends ObserverBase {
    method = SignalRMethod.webConferencePresentersUpdated;

    constructor(
        protected readonly data: ObserverData,
        private readonly client: LiveKitClientInterface,
        private readonly stateHelper: WebConferenceStateHelper,
        private readonly webConferenceService: WebConferenceService,
        private readonly storeHelper: StoreHelper,
        private readonly participantsEventHandler: LiveKitParticipantsEventsHandler
    ) {
        super(data);
    }

    async callback(data: any): Promise<void> {
        console.log('presenters update', data);
        try {
            const update = JSON.parse(data) as RealtimeUpdate;

            if (update && update.users && update.users.length) {
                const user = update.users[0];
                const isCurrentUser = user.userId == this.data.store.getters['userId'];

                if (isCurrentUser) {
                    if (update.type == "added") {
                        this.stateHelper.showEventInfo = false;
                        this.stateHelper.needDeviceTest = true;
                        await this.webConferenceService.lowerHandIfRaised();
                    }

                    if (this.webConferenceService?.isWebConferenceDataNotEmpty()) {
                        if(update.type == "banned") {
                            this.stateHelper.banned = true;
                            await this.client.leave();
                        }

                        if (update.type == "removed") {
                            this.stateHelper.needDeviceTest = false;
                            await this.client.changeRoleToAudience();
                        }

                        this.webConferenceService.setNewRole(update.type == "added" ? UserRoleType.Presenter : UserRoleType.Audience);
                    }
                }

                if (update.type == "removed" && update.users && update.users.length && update.users[0].userId) {
                    this.storeHelper.removeJoinedUser(update.users[0].userId as number);
                    this.client.tracksManager.remove(update.users[0].userId.toString());
                }

                this.data.store.dispatch(update.type == "added" ? addPresenterAction : removePresenterAction, {
                    key: this.data.stateKey,
                    user: new UserProfile(user)
                } as AddPresentersParam);
            }
        }
        catch (e) {
            this.data.store.dispatch('reportError', e);
            console.error(e);
        }
    }

    async processConnect(connectResult: RealtimeUpdate) {
        if (connectResult) {
            const presenters = connectResult.users
                ? connectResult.users
                    .filter(item => !item.isDeleted)
                    .map(item => new UserProfile(item))
                : [];
            const removedSpeakers: number[] = connectResult.users
                ? connectResult.users
                    .filter(item => item.isDeleted)
                    .map(item => item.userId as number)
                : [];

            this.data.store.dispatch(setPresentersAction, {
                key: this.data.stateKey,
                users: presenters
            } as SetPresentersParam);

            this.data.store.dispatch(addRemovedSpeakers, {
                key: this.data.stateKey,
                data: removedSpeakers
            } as StoreParameters<number | number[]>);
        }
    }
}
